import React, { useEffect, useState } from "react";
import {
  Grid,
  Header,
  Menu,
  Modal,
  Popup,
  Button,
  Icon,
  Segment,
  Dimmer,
  Loader,
  Input,
} from "semantic-ui-react";
import axios from "../../../../../../../apis/dvdlocator";
import { useDispatch } from "react-redux";
import { addNotification } from "../../../../../../../redux/actions/notificationActions";

const FamilyPanel = ({
  family,
  open,
  close,
  updateFamilyName,
  deleteFamilyFromState,
}) => {
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [supply, setSupply] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [familyName, setFamilyName] = useState(family.family);
  const [submitting, setSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getMembers = async () => {
      try {
        const res = await axios.get(`/families/${family.id}/members`);

        // Updated to handle the new API response structure
        setProducts(res.data.products || []);
        setSupply(res.data.supply || []);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    getMembers();
  }, [family]);

  const editFamilyName = async () => {
    setSubmitting(true);
    try {
      await axios.put(`/families/${family.id}`, { name: familyName });

      updateFamilyName(family.id, familyName);

      dispatch(
        addNotification({
          text: "Saved new family name!",
          icon: <Icon color="green" name="checkmark box" />,
        })
      );
    } catch (err) {
      if (err.response) {
        if (err.response.status === 409) {
          return setEditError("This family name already exists.");
        }
      }
      dispatch(
        addNotification({
          text: `Error saving family name.`,
          icon: <Icon color="red" name="warning circle" />,
        })
      );
    } finally {
      setSubmitting(false);
      setEditingName(false);
    }
  };

  const deleteFamily = async () => {
    setSubmitting(true);

    try {
      await axios.delete(`/families/${family.id}`);

      deleteFamilyFromState(family.id);

      dispatch(
        addNotification({
          text: "Deleted family!",
          icon: <Icon color="green" name="checkmark box" />,
        })
      );

      close();
    } catch (err) {
      console.log(err);

      dispatch(
        addNotification({
          text: `Error deleting family.`,
          icon: <Icon color="red" name="warning circle" />,
        })
      );
    } finally {
      setSubmitting(false);
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={close}>
      {deleting && (
        <Modal open={deleting} size="tiny">
          <Modal.Header>Delete Family</Modal.Header>

          <Modal.Content>
            <h3 style={{ color: "red" }}>
              Are you sure you want to delete this family?
            </h3>
            <b>{`(Only removes family associations - doesn't delete product/listing rows)`}</b>
            <p style={{ marginTop: "16px" }}>Family: {family.family}</p>
            <p>Products: {products.length}</p>
            <p>Supply Units: {supply.length}</p>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={() => setDeleting(false)}>Cancel</Button>
              <Button
                disabled={submitting}
                color="red"
                onClick={() => {
                  deleteFamily(family.id);
                }}
              >
                Delete
              </Button>
            </div>
          </Modal.Content>
        </Modal>
      )}
      <Modal.Header>
        <Grid columns={2}>
          <Grid.Column floated="left" verticalAlign="middle">
            {editingName ? (
              <>
                {editError && (
                  <span style={{ color: "red", marginBottom: "8px" }}>
                    {editError}
                  </span>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5em",
                  }}
                >
                  <Input
                    size="mini"
                    focus
                    disabled={submitting}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") editFamilyName();
                    }}
                    onChange={(e) => setFamilyName(e.target.value)}
                    value={familyName}
                  />
                  <Icon
                    name="check"
                    color="green"
                    disabled={submitting}
                    onClick={editFamilyName}
                    style={{ cursor: "pointer" }}
                    title="Save"
                  />
                  <Icon
                    name="times"
                    color="red"
                    disabled={submitting}
                    onClick={() => setEditingName(false)}
                    style={{ cursor: "pointer" }}
                    title="Cancel"
                  />
                </div>
              </>
            ) : (
              <Header floated="left">{family.family}</Header>
            )}
          </Grid.Column>
          <Grid.Column floated="right">
            <Popup
              open={popupOpen}
              basic
              trigger={
                <Button
                  icon="ellipsis horizontal"
                  circular
                  floated="right"
                  onClick={() => setPopupOpen((prev) => !prev)}
                />
              }
              on="click"
            >
              <Menu vertical fluid>
                <Menu.Item
                  name="inbox"
                  onClick={() => {
                    setEditingName(true);
                    setPopupOpen(false);
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#dcdcdc")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#f8f8f8")
                  }
                  className="no-pointer"
                >
                  <Icon name="edit outline" />
                  Edit Name
                </Menu.Item>

                <Menu.Item
                  name="inbox"
                  onClick={() => {
                    setDeleting(true);
                    setPopupOpen(false);
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#dcdcdc")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#f8f8f8")
                  }
                  className="no-pointer"
                >
                  <Icon name="trash alternate outline" />
                  Delete Family
                </Menu.Item>
              </Menu>
            </Popup>
          </Grid.Column>
        </Grid>
      </Modal.Header>
      <Modal.Content>
        {loading ? (
          <Segment padded="very">
            <Dimmer active inverted>
              <Loader active inline="centered" content="Loading" />
            </Dimmer>
          </Segment>
        ) : (
          <div>
            <Segment>
              <Header>{products.length} products</Header>
              <div style={{ overflowX: "auto", maxHeight: "200px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        ASIN
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        Title
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.product_id}>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {product.product_asin || "-"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {product.product_title}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Segment>

            <Segment>
              <Header>{supply.length} supply units</Header>
              <div style={{ overflowX: "auto", maxHeight: "200px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        UPC
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        ASIN
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        Title
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {supply.map((item, index) => (
                      <tr key={`${item.upc}-${index}`}>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {item.upc || "-"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {item.supply_asin || "-"}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {item.supply_title}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Segment>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "16px",
          }}
        >
          <Button primary onClick={close}>
            Done
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default FamilyPanel;
